import { gql } from "generated-graphql";

export const SUBMISSION_VIRTUAL_EVENTS_QUERY = gql(`
  query SubmissionVirtualEvents($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    submissionVirtualEvents(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        id
        seriesId
        taskId
        frequency
        interval
        title
        facilities {
          id
          name
        }
        status
        deadline
        assignee {
          ...AssigneePicker
        }
        tags {
          name
        }
        submission {
            id
            tenantId
            title
            submissionType
            status
            signatureType
            dueDate
            submittedAt
            notes
            facilitySubmissions {
            id
            facility {
                id
                name
            }
            }
            staffAssignee {
                id
                email
                person {
                    id
                    first
                    last
                }
            }
            reviewers {
                id
                user {
                    id
                    email
                    type
                    person {
                        id
                        first
                        last
                    }
                }
                isSigner
                isStaff
            }
            documents {
                id
                document {
                    title
                }
            }
            createdAt
            updatedAt
        }
      }
    }
  }
`);

export const CREATE_SUBMISSION_MUTATION = gql(`
  mutation CreateSubmission($input: CreateSubmissionInput!) {
    createSubmission(input: $input) {
      id
      title
      submissionType
      recipient
      submissionMethod
      dueDate
      reportingPeriodStart
      reportingPeriodEnd
      notes
      reviewRequired
      reviewMode
      signatureType
      status
      createdAt
      updatedAt
      tenantId
      staffAssignee {
        id
        email
        person {
          first
          last
        }
      }
      facilitySubmissions {
        id
        facility {
          id
          name
          streetAddress1
          city
          state
        }
      }
      reviewers {
        id
        user {
          id
          email
          person {
            first
            last
          }
          facilities {
            id
          }
        }
        reviewOrder
        isRequired
        isSigner
        isStaff
        dueDate
      }
      documents {
        id
        type
        status
        document {
          id
          title
          documentType
          storageLink
          description
        }
      }
    }
  }
`);

export const UPDATE_SUBMISSION_MUTATION = gql(`
  mutation UpdateSubmission($input: UpdateSubmissionInput!) {
    updateSubmission(input: $input) {
      id
      title
      recipient
      submissionMethod
      dueDate
      reportingPeriodStart
      reportingPeriodEnd
      notes
      reviewRequired
      reviewMode
      signatureType
      status
      updatedAt
      tenantId
      staffAssignee {
        id
        email
        person {
          first
          last
        }
      }
      facilitySubmissions {
        id
        facility {
          id
          name
          streetAddress1
          city
          state
        }
      }
      reviewers {
        id
        user {
          id
          email
          person {
            first
            last
          }
          facilities {
            id
          }
        }
        reviewOrder
        isRequired
        isSigner
        isStaff
        dueDate
      }
      documents {
        id
        type
        status
        document {
          id
          title
          documentType
          storageLink
          description
        }
      }
    }
  }
`);

export const MOVE_TO_REVIEW_MUTATION = gql(`
  mutation MoveToReview($input: UpdateSubmissionInput!) {
    moveToReview(input: $input) {
      id
      title
      recipient
      submissionMethod
      dueDate
      reportingPeriodStart
      reportingPeriodEnd
      notes
      reviewRequired
      reviewMode
      signatureType
      status
      updatedAt
      tenantId
      facilitySubmissions {
        id
        facility {
          id
          name
          streetAddress1
          city
          state
        }
      }
      reviewers {
        id
        user {
          id
          email
          person {
            first
            last
          }
          facilities {
            id
          }
        }
        reviewOrder
        isRequired
        isSigner
        isStaff
        dueDate
      }
      documents {
        id
        type
        status
        document {
          id
          title
          documentType
          storageLink
          description
        }
      }
    }
  }
`);

export const GET_SUBMISSION_QUERY = gql(`
  query GetSubmission($id: ID!) {
    submission(id: $id) {
      id
      title
      submissionType
      recipient
      submissionMethod
      dueDate
      reportingPeriodStart
      reportingPeriodEnd
      notes
      reviewRequired
      reviewMode
      signatureType
      status
      createdAt
      updatedAt
      staffAssignee {
        id
        email
        person {
          first
          last
        }
      }
      facilitySubmissions {
        id
        facility {
          id
          name
          streetAddress1
          city
          state
          customerFacilityId
        }
      }
      reviewers {
        id
        isSigner
        completedAt
        user {
          id
          email
          person {
            first
            last
          }
          facilities {
            id
          }
        }
        submissionDocumentReviews {
            id
            submissionReviewerId
            submissionReviewer {
              userId
              isSigner
            }
            submissionDocumentId
            submissionDocument {
              id
              externalDocumentId
            }
            reviewerStatus
            signatureStatus
            completedAt
            hasViewed
            createdAt
        }
        reviewOrder
        isRequired
        isSigner
        isStaff
        dueDate
        completedAt
      }
      documents {
        id
        type
        status
        submissionDocumentReviews {
            id
            submissionReviewerId
            submissionReviewer {
              userId
              isSigner
            }
            submissionDocumentId
            reviewerStatus
            signatureStatus
            completedAt
            hasViewed
        }
        document {
          id
          title
          documentType
          storageLink
          description
        }
      }
      comments {
        ...CommentThread
      }
      payments {
        id
        payment {
          id
          amount
          payee
          paymentMethod
          paidAt
          status
        }
      }
      issues {
        ...issue
      }
    }
  }
`);

export const DELETE_SUBMISSION_MUTATION = gql(`
  mutation DeleteSubmission($id: ID!) {
    deleteSubmission(id: $id)
  }
`);

export const APPROVE_SUBMISSION_DOCUMENT_REVIEW_MUTATION = gql(`
  mutation ApproveSubmissionDocumentReview($submissionDocumentReviewId: ID!) {
    approveSubmissionDocumentReview(
      submissionDocumentReviewId: $submissionDocumentReviewId
    ) {
      id
      reviewerStatus
      signatureStatus
      completedAt
      submissionReviewerId
      submissionDocumentId
      submissionDocument {
        id
        submissionId
        status
      }
    }
  }
`);

export const REJECT_SUBMISSION_DOCUMENT_REVIEW_MUTATION = gql(`
  mutation RejectSubmissionDocumentReview($submissionDocumentReviewId: ID!) {
    rejectSubmissionDocumentReview(
      submissionDocumentReviewId: $submissionDocumentReviewId
    ) {
      id
      reviewerStatus
      signatureStatus
      completedAt
      submissionReviewerId
      submissionDocumentId
      submissionDocument {
        id
        submissionId
        status
      }
    }
  }
`);

export const SUBMISSION_FACILITIES_QUERY = gql(`
  query SubmissionFacilities($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        customerFacilityId
        streetAddress1
        city
        state
        zip
      }
      count
    }
  }
`);

export const GET_EMBEDDED_DOCUMENT_PREPARATION_URL = gql(`
  query GetEmbeddedDocumentPreparationUrl($submissionId: ID!) {
    getEmbeddedDocumentPreparationUrl(submissionId: $submissionId)
  }
`);

export const GET_EMBEDDED_DOCUMENT_SIGNING_URL = gql(`
  query GetEmbeddedDocumentSigningUrl($submissionId: ID!) {
    getEmbeddedDocumentSigningUrl(submissionId: $submissionId)
  }
`);

export const GET_VIRTUAL_EVENT_FOR_SUBMISSION_QUERY = gql(`
  query GetVirtualEventForSubmission($taskId: ID!, $deadline: DateTime!) {
    virtualEvent(taskId: $taskId, deadline: $deadline) {
      id
      taskId
      seriesId
      title
      description
      deadline
      completedAt
      reminderDays
      frequency
      interval
      facilities {
        id
        name
      }
      assignee {
        id
        email
        person {
          first
          last
        }
      }
      watchers {
        id
        email
        person {
          first
          last
        }
      }
      documents {
        id
        fileExtension
        title
        documentType
      }
      tags {
        id
        name
      }
      submission {
        id
        title
        submissionType
        recipient
        submissionMethod
        notes
      }
      links
    }
  }
`);

export const UPDATE_SUBMISSION_STATUS_MUTATION = gql(`
  mutation UpdateSubmissionStatus($input: UpdateSubmissionStatusInput!) {
    updateSubmissionStatus(input: $input) {
      id
      status
      updatedAt
    }
  }
`);

export const BULK_SUBMISSIONS_SAFE_TO_UPDATE_QUERY = gql(`
  query BulkSubmissionsSafeToUpdate($submissionIds: [ID!]!, $targetStatus: SubmissionStatus!) {
    bulkSubmissionsSafeToUpdate(submissionIds: $submissionIds, targetStatus: $targetStatus) {
      id
      isSafe
      reasons
      submission {
        id
        title
        submissionType
        status
        dueDate
        facilitySubmissions {
          id
          facility {
            id
            name
          }
        }
      }
    }
  }
`);

export const BULK_UPDATE_SUBMISSIONS_STATUS_MUTATION = gql(`
  mutation BulkUpdateSubmissionsStatus(
    $input: BulkUpdateSubmissionsStatusInput!
  ) {
    bulkUpdateSubmissionsStatus(input: $input) {
      succeeded
      jobId
    }
  }
`);

export const SET_SUBMISSION_ASSIGNEES_MUTATION = gql(`
  mutation SetSubmissionAssignees($submissionIds: [ID!]!, $assigneeId: ID!) {
    setSubmissionAssignees(submissionIds: $submissionIds, assigneeId: $assigneeId) {
      succeeded
      updated
      failed
    }
  }
`);

export const BULK_SUBMISSIONS_SAFE_TO_SEND_TO_REVIEWERS_QUERY = gql(`
  query BulkSubmissionsSafeToSendToReviewers($submissionIds: [ID!]!) {
    bulkSubmissionsSafeToSendToReviewers(submissionIds: $submissionIds) {
      id
      isSafe
      reasons
      submission {
        id
        title
        submissionType
        status
        dueDate
        facilitySubmissions {
          id
          facility {
            id
            name
          }
        }
      }
    }
  }
`);

export const SEND_SUBMISSIONS_TO_REVIEWERS_MUTATION = gql(`
  mutation SendSubmissionsToReviewers($submissionIds: [ID!]!) {
    sendSubmissionsToReviewers(submissionIds: $submissionIds) {
      succeeded
      jobId
    }
  }
`);
